import { Suspense, lazy, type ComponentType } from 'react';
import { Routes, Route } from 'react-router-dom';
import { PAGES, type PageDef } from '@/constants/pages';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { RequireRole } from '@/components/auth/RequireRole';
import { RootRedirect } from '@/components/auth/RootRedirect';
import { ErrorBoundary } from '@/components/app/ErrorBoundary';
import { AppBootstrapLoader } from '@/components/app/AppBootstrapLoader';
import { AppErrorFallback } from '@/components/app/AppErrorFallback';
import { NotFound } from '@/components/app/NotFound';

// Lazy components and the entries array are created once at module scope.
// Placing them inside the render function would allocate a new lazy()
// reference on every re-render of AppRoutes (which happens whenever
// BrowserRouter re-renders due to a location change), causing React to
// treat the component type as changed, unmount the current page, and reset
// the Suspense boundary — resulting in a blank screen after every navigation.
const PAGE_ENTRIES = Object.entries(PAGES) as Array<[string, PageDef]>;
const LAZY_COMPONENTS = Object.fromEntries(
  PAGE_ENTRIES.map(([key, page]) => [key, lazy(page.load)]),
) as Record<string, ComponentType>;

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      {PAGE_ENTRIES.map(([key, page]) => {
        // key is always present — PAGE_ENTRIES and LAZY_COMPONENTS share the same keyset
        const LazyComponent = LAZY_COMPONENTS[key] as ComponentType;
        const element = (
          <ErrorBoundary fallback={<AppErrorFallback />}>
            <Suspense fallback={<AppBootstrapLoader />}>
              <LazyComponent />
            </Suspense>
          </ErrorBoundary>
        );

        const guarded = page.isPublic
          ? element
          : page.roles
          ? (
              <RequireAuth>
                <RequireRole roles={page.roles}>{element}</RequireRole>
              </RequireAuth>
            )
          : <RequireAuth>{element}</RequireAuth>;

        return <Route key={key} path={page.path} element={guarded} />;
      })}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
